import { Component, EventEmitter, Input, OnInit, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import { CrewChangeServices} from '../crew-change.service';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { SharedModule } from 'src/app/shared/shared.module';
import { AgGridModule } from 'ag-grid-angular';
import { AgGridService } from 'src/app/shared/ag-grid/ag-grid.service';
import { GridOptions, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { Router } from '@angular/router';
import { DocumentService } from 'src/app/documents/documents.service';
import { UserLocalStoreService } from 'src/app/shared/services/user-local-store.service';
import { Crew, CrewDocument, CrewChange } from 'src/app/generated/services/transportation/crew/crew';

@Component({
  selector: 'app-crew-changes',
  templateUrl: './crew-changes.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule, AgGridModule]
})
export class CrewChangeListComponent implements OnInit {

  title: EventEmitter<string> = new EventEmitter();

  gridOptions: GridOptions = {};
  gridRestored: boolean = false;
  _crews: Crew[] = [];
  _organisationsMap: Map<string, Organisation> = new Map();
  getCountryCode = this._organisationService.getCountryCode;

  @Input() set crews(crews: Crew[]) {
    if (crews) {
      this._crews = crews;
      this._crews.forEach(crew => {
        crew.crewChanges.sort((a, b) => Math.max(...b.item.map(i => i.time)) - Math.max(...a.item.map(i => i.time)));
      });
    }
  }

  @Input() set organisationsMap(organisationsMap: Map<string, Organisation>) {
    if (organisationsMap && this._crews) {
      this._organisationsMap = organisationsMap;
    }
  }

  constructor(private _crewChangeService: CrewChangeServices,
    private _organisationService: OrganisationService,
    private _renderer: Renderer2,
    private _documentsService: DocumentService,
    private _router: Router,
    private _userLocalStorage: UserLocalStoreService,
  ) { }

  columnDefs = [
    {
      headerName: 'Name',
      width: 460,

      valueGetter: (params:ICellRendererParams) => `${params.data?.firstName} ${params.data?.lastName}`,
      cellRenderer: (params:ICellRendererParams) => {
        const div = this._renderer.createElement('div');
        const countrySpan = this._renderer.createElement('span');
        const countryCode = this.getCountryCode(params.data.nationality);

        const profileImg = this._renderer.createElement('img');
        this._renderer.addClass(profileImg, 'active');
        this._renderer.addClass(profileImg, 'img-size');
        this._renderer.addClass(profileImg, 'ms-1');
        this._renderer.addClass(profileImg, 'rounded-circle');

        let src = '/assets/icons/main-logo/person.jpg';
        if (params.data.photoDocumentId) {
          this._documentsService.download(params.data.photoDocumentId).subscribe(b => {
            src = URL.createObjectURL(b.body!);
            this._renderer.setAttribute(profileImg, 'src', src);
          });
        }
        this._renderer.setAttribute(profileImg, 'src', src);

        const fullName = `${params.data?.firstName} ${params.data?.lastName}`;
        this._renderer.addClass(countrySpan, countryCode[0]);
        this._renderer.addClass(countrySpan, countryCode[1]);

        const text = AgGridService.editButtonWithText(
          this._renderer,
          this._router,
          this.edit,
          params.data,
          fullName
        );

        const statusSpan = this._renderer.createElement('span');
        const status = this.getActivationStatus(params.data).split(' ');
        this._renderer.addClass(statusSpan, status[0]);
        this._renderer.addClass(statusSpan, status[1]);
        this._renderer.addClass(statusSpan, status[2]);
        this._renderer.addClass(statusSpan, status[3]);

        this._renderer.appendChild(div, countrySpan);
        this._renderer.appendChild(div, profileImg);
        this._renderer.appendChild(div, text);
        this._renderer.appendChild(div, statusSpan);

        return div;

      },

      filterValueGetter: (params:ICellRendererParams) => {
        const status = this._crewChangeService.getLatestStatus(params.data);
        return `${params.data?.firstName} ${params.data?.lastName} ${status}`;
      }
    },
    {
      headerName: 'Passport',
      field: 'passportDetails',
      cellRenderer: (params:ICellRendererParams) => this.changeDateColor(params.data.passportDetails),
      filterValueGetter: (params:ICellRendererParams) => params.data.passportDetails,
    },

    {
      headerName: 'Sea Man',
      cellRenderer: (params:ICellRendererParams) => this.changeDateColor(params.data.seamanDetails)
    },

    {
      headerName: 'Status',
      valueGetter: (params:ICellRendererParams) => this.getStatus(params.data)
    },

    {
      headerName: 'Onsigns',
      valueGetter: (params:ICellRendererParams) => this.numberOfCrewChanges(params.data.crewChanges)
    },
    {
      headerName: 'Appraisals',
      valueGetter: (params:ICellRendererParams) => this.numberOfAppraisals(params.data.crewChanges)
    },

    {
      headerName: 'Rank',
      valueGetter: (params:ICellRendererParams) => {
        if (params.data?.crewChanges?.length > 0) {
          return this.getLastCrewChange(params.data.crewChanges)?.rank || '';
        }
        return '';
      }
    },

    {
      headerName: 'Vessel Name',
      valueGetter: (params:ICellRendererParams) => {
        if (params.data?.crewChanges?.length > 0) {
          return this.getLastCrewChange(params.data.crewChanges)?.vesselName || '';
        }
        return '';
      }
    },

    {
      headerName: 'Management',
      valueGetter: (params:ICellRendererParams) => (params.data.crewChanges.length > 0) ? this.getOrganisationNameById(
        params.data.crewChanges[0]?.contract?.managementOrganisationId) : ''
    },

    {
      headerName: 'On Board',
      valueGetter: (params:ICellRendererParams) => (params.data.crewChanges.length > 0) ? `${this.planPlace(params.data.crewChanges[0], 'On Board')} - ${this.planTime(params.data.crewChanges[0], 'On Board')}` : ''
    },

    {
      headerName: 'Off Board',
      valueGetter: (params:ICellRendererParams) => (params.data.crewChanges.length > 0) ? `${this.planPlace(params.data.crewChanges[0], 'Off Board')} - ${this.planTime(params.data.crewChanges[0], 'Off Board')}` : ''
    },

    {
      headerName: 'Months Onboard',
      valueGetter: (params:ICellRendererParams) => (params.data.crewChanges.length > 0) ? this.onboardTimeMonthsDays(params.data.crewChanges[0]) : ''
    },
  ];

  ngOnInit(): void {
    this.gridOptions = AgGridService.getOptions();
    this.title.emit('Crew Change List');
  }

  changeDateColor(doc: CrewDocument): string {
    const dateClass = (!doc.expirationDate || new Date() > new Date(doc.expirationDate)) ? 'text-red' : 'text-green';
    const text = `${doc.number} - ${doc.expirationDate}`;
    const dateSpan = this._renderer.createElement('span');
    this._renderer.addClass(dateSpan, dateClass);
    this._renderer.appendChild(dateSpan, this._renderer.createText(text));
    return dateSpan;
  }

  edit(router: Router, crew: Crew) {
    router.navigate(['/vessels/crew-change/crew/profile/', crew.id]).then();
  }

  gridSetting(event: GridReadyEvent) {
    AgGridService.setting(event);
  }

  saveGridState(event: GridReadyEvent) {
    AgGridService.saveGridState(event, this.gridRestored, this._userLocalStorage, 'crewGrid');
  }

  restoreGridState(event: GridReadyEvent) {
    this.gridRestored = AgGridService.restoreGridState(event, this._userLocalStorage, 'crewGrid');
  }

  // restoreGridToDefaultState(event: GridReadyEvent) {
  //   AgGridService.restoreGridToDefaultState(this.gridApi, this._userLocalStorage, 'crewGrid');
  // }

  getOrganisationNameById(id: string) {
    if (this._organisationsMap && id) {
      return this._organisationsMap.get(id)?.nameTranslations['en'];
    }
    return 'Unknown';
  }

  onboardTimeMonthsDays(crewChange: CrewChange) {
    return this._crewChangeService.onboardTimeMonthsDays(crewChange);
  }

  travelTimeDays(crewChange: CrewChange) {
    return this._crewChangeService.travelTimeDays(crewChange);
  }

  planTime(crewChange: CrewChange, selectedPlan: string) {
    if (this._organisationsMap) {
      return this._crewChangeService.planTime(crewChange, selectedPlan, this._organisationsMap);
    }
  }

  planPlace(crewChange: CrewChange, selectedPlan: string) {
    if (this._organisationsMap) {
      return this._crewChangeService.planPlace(crewChange, selectedPlan, this._organisationsMap);
    }
  }

  getStatus(crew: Crew) {
    if (crew) {
      return this._crewChangeService.getCrewStatus(crew);
    }
  }

  numberOfCrewChanges(crewChanges: CrewChange[]) {
    return crewChanges.filter(cr => !cr.isDeleted).length;
  }

  numberOfAppraisals(crewChanges: CrewChange[]) {
    return crewChanges.filter(c => c.appraisals && c.appraisals.length > 0).flatMap(c => c.appraisals).length;
  }

  getLastCrewChange(crewChanges: CrewChange[]) {
    if (crewChanges.length > 0) {
      return this._crewChangeService.getLastCrewChange(crewChanges);
    } else {
      return null;
    }
  }
  getActivationStatus(crew: Crew) {
    return this._crewChangeService.getCrewActivationStatus(crew);
  }
}
